// MDR service SLA contract (SLA del servicio MDR, vigente).
// "Critical" in the contract = Cortex severities critical + high.
export const SLA_CONTRACT = {
  criticalSeverities: ['critical', 'high'],
  // Bogota is UTC-5 all year (no DST)
  utcOffsetHours: -5,
  businessHours: { start: 8, end: 18 },
  mttd: {
    critical: { minutes: 60, target: 100, businessHours: false },
    nonCritical: { minutes: 120, target: 90, businessHours: false }
  },
  mttr: {
    critical: { minutes: 90, target: 100, businessHours: false },
    // 8x5xNBD: only business minutes (Mon-Fri, not holidays) count
    nonCritical: { minutes: 180, target: 90, businessHours: true }
  }
}

const MINUTE = 60 * 1000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

function dayKey (date) {
  return date.toISOString().slice(0, 10)
}

function addDays (date, days) {
  return new Date(date.getTime() + days * DAY)
}

function nextMonday (date) {
  return addDays(date, (8 - date.getUTCDay()) % 7)
}

// Anonymous Gregorian algorithm
function easterSunday (year) {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(Date.UTC(year, month - 1, day))
}

const holidayCache = {}

// Colombian public holidays (Ley 51 de 1983 "Ley Emiliani"), as YYYY-MM-DD
export function colombianHolidays (year) {
  if (holidayCache[year]) return holidayCache[year]
  const on = (month, day) => new Date(Date.UTC(year, month - 1, day))
  const fixed = [on(1, 1), on(5, 1), on(7, 20), on(8, 7), on(12, 8), on(12, 25)]
  const movedToMonday = [on(1, 6), on(3, 19), on(6, 29), on(8, 15), on(10, 12), on(11, 1), on(11, 11)].map(nextMonday)
  const easter = easterSunday(year)
  // Holy Thursday, Good Friday, Ascension, Corpus Christi, Sacred Heart (last three already on Monday)
  const easterBased = [-3, -2, 43, 64, 71].map(offset => addDays(easter, offset))
  holidayCache[year] = new Set([...fixed, ...movedToMonday, ...easterBased].map(dayKey))
  return holidayCache[year]
}

export function isBusinessDay (localDate) {
  const dow = localDate.getUTCDay()
  return dow !== 0 && dow !== 6 && !colombianHolidays(localDate.getUTCFullYear()).has(dayKey(localDate))
}

// Minutes between two UTC epoch-ms instants that fall inside Bogota business hours
export function businessMinutesBetween (startMs, endMs, contract = SLA_CONTRACT) {
  if (!(endMs > startMs)) return 0
  const offset = contract.utcOffsetHours * HOUR
  const start = startMs + offset
  const end = endMs + offset
  let total = 0
  for (let day = start - (start % DAY); day < end; day += DAY) {
    if (!isBusinessDay(new Date(day))) continue
    const open = day + contract.businessHours.start * HOUR
    const close = day + contract.businessHours.end * HOUR
    total += Math.max(0, Math.min(close, end) - Math.max(open, start))
  }
  return total / MINUTE
}

export function elapsedMinutes (startMs, endMs, businessHours, contract = SLA_CONTRACT) {
  return businessHours ? businessMinutesBetween(startMs, endMs, contract) : (endMs - startMs) / MINUTE
}

export function severityGroup (severity, contract = SLA_CONTRACT) {
  return contract.criticalSeverities.includes(severity) ? 'critical' : 'nonCritical'
}

// Cloud posture / vulnerability scanner findings (Cortex Cloud modules, enabled 2026-05),
// not threat detections. Values of incident.incident.incident_sources in mdr.cortex_incidents.
export const POSTURE_SOURCES = [
  'CSPM Scanner', 'CIEM Scanner', 'Cloud Network Analyzer', 'API Posture Scanner',
  'CAS CVE Scanner', 'CAS License Scanner', 'CAS Secret Scanner', 'IaC Scanner', 'CI/CD Risks',
  'Vulnerability Policy', 'Package Operational Risk', 'AppSec Policy', 'Data Policy', 'Compute Policy'
]

export function isPostureFinding (sources) {
  return Array.isArray(sources) && sources.length > 0 && sources.every(source => POSTURE_SOURCES.includes(source))
}

// Tooltip shown on the SLA/SLO pages, which leave these findings out of every metric
export function postureExclusionNote (count) {
  return `Excludes ${count} cloud posture and vulnerability findings (Cortex Cloud scanners: ` +
    'CSPM, CVE, vulnerability and policy scanners).'
}

// MTTR event: first status change away from "new" (by360 mttr_time), else the resolution.
// If the status left "new" but the sync never saw it, the last modification is an upper
// bound: the real response happened at or before it, so this never overstates compliance.
export function responseMs (inc) {
  return inc.mttr_ms || inc.resolved_ms || (inc.status !== 'new' ? inc.modification_ms : null) || null
}

// MTTD event: detection is accomplished when the case leaves "new" (set to under_investigation
// by the 5-minute workflow or by an analyst), so it is the same status change
export function detectionMs (inc) {
  return responseMs(inc)
}

// incidents: [{ severity, status, creation_ms, modification_ms, mttr_ms, resolved_ms }]
// Incidents with no event yet count as missed once past the threshold, and as pending before that.
export function evaluateIndicator (indicator, incidents, nowMs, contract = SLA_CONTRACT) {
  const rules = contract[indicator]
  const eventMs = indicator === 'mttd' ? detectionMs : responseMs
  const result = {}
  Object.keys(rules).forEach(group => {
    result[group] = { met: 0, missed: 0, missedNoResponse: 0, pending: 0 }
  })
  incidents.forEach(inc => {
    if (!inc.creation_ms) return
    const group = severityGroup(inc.severity, contract)
    const rule = rules[group]
    const stats = result[group]
    const event = eventMs(inc)
    if (event) {
      const minutes = elapsedMinutes(inc.creation_ms, event, rule.businessHours, contract)
      minutes <= rule.minutes ? stats.met++ : stats.missed++
    } else if (elapsedMinutes(inc.creation_ms, nowMs, rule.businessHours, contract) > rule.minutes) {
      stats.missed++
      stats.missedNoResponse++
    } else {
      stats.pending++
    }
  })
  Object.keys(result).forEach(group => {
    const stats = result[group]
    stats.total = stats.met + stats.missed
    stats.pct = stats.total ? (stats.met / stats.total) * 100 : null
    stats.targetMet = stats.pct !== null && stats.pct >= rules[group].target
  })
  return result
}

export function evaluateMttr (incidents, nowMs, contract = SLA_CONTRACT) {
  return evaluateIndicator('mttr', incidents, nowMs, contract)
}

export function evaluateMttd (incidents, nowMs, contract = SLA_CONTRACT) {
  return evaluateIndicator('mttd', incidents, nowMs, contract)
}
